import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchDataFromAPI } from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ/api/dataGridApiService';
import { formatUtcDateKeyLabel } from 'utils/dateUtils';
import type {
  FilterTrayCategory,
  FilterTrayOption,
  FilterTraySelection,
} from 'app/components/ReusableUIComponents/HighLevelComponents/FilterTrayMAIQ';

const EXECUTIONS_ENDPOINT =
  'api/v2/dealer-settings/nurturing-plan/executions/datagrid';
const FILTER_TRAY_PAGE_SIZE = 500;
const EXECUTION_STATUS_FIELD = 'execution_status';
const CALENDAR_DATE_FIELD = 'calendar_date';

export type NurturingPlanExecutionFilterModel = {
  items: Array<{
    id?: number;
    columnField: string;
    operatorValue: string;
    value?: unknown;
  }>;
  linkOperator?: 'and' | 'or';
  orGroups?: Array<{
    items: Array<{
      id?: number;
      columnField: string;
      operatorValue: string;
      value?: unknown;
    }>;
  }>;
};

function toStr(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return String(value);
}

function formatExecutionStatusLabel(value: string): string {
  return value
    .split('_')
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

function deriveExecutionStatusValues(rows: unknown[]): string[] {
  const values = new Set<string>();
  for (const row of rows) {
    const status = toStr((row as Record<string, unknown>)?.execution_status).trim();
    if (status) values.add(status);
  }
  return Array.from(values).sort((a, b) => a.localeCompare(b));
}

function deriveCalendarDateKeys(rows: unknown[]): string[] {
  const values = new Set<string>();
  for (const row of rows) {
    const dateKey = toStr((row as Record<string, unknown>)?.calendar_date).trim();
    if (dateKey) values.add(dateKey);
  }
  return Array.from(values).sort((a, b) => b.localeCompare(a));
}

function buildExecutionStatusFilterModel(
  selectedStatuses: string[] | undefined,
): NurturingPlanExecutionFilterModel | undefined {
  if (!Array.isArray(selectedStatuses) || selectedStatuses.length === 0) {
    return undefined;
  }
  if (selectedStatuses.length === 1) {
    return {
      items: [
        {
          id: 1,
          columnField: EXECUTION_STATUS_FIELD,
          operatorValue: 'equals',
          value: selectedStatuses[0],
        },
      ],
      linkOperator: 'and',
    };
  }
  return {
    items: [
      {
        id: 1,
        columnField: EXECUTION_STATUS_FIELD,
        operatorValue: 'isAnyOf',
        value: selectedStatuses,
      },
    ],
    linkOperator: 'and',
  };
}

function buildCalendarDateFilterModel(
  selectedDateKeys: string[] | undefined,
): NurturingPlanExecutionFilterModel | undefined {
  if (!Array.isArray(selectedDateKeys) || selectedDateKeys.length === 0) {
    return undefined;
  }

  if (selectedDateKeys.length === 1) {
    return {
      items: [
        {
          id: 1,
          columnField: CALENDAR_DATE_FIELD,
          operatorValue: 'equals',
          value: selectedDateKeys[0],
        },
      ],
      linkOperator: 'and',
    };
  }

  const dateItems = selectedDateKeys.map((dateKey, index) => ({
    id: index + 1,
    columnField: CALENDAR_DATE_FIELD,
    operatorValue: 'equals',
    value: dateKey,
  }));

  return {
    items: dateItems,
    linkOperator: 'or',
  };
}

function buildCombinedExecutionFilterModel(
  statusModel?: NurturingPlanExecutionFilterModel,
  calendarDateModel?: NurturingPlanExecutionFilterModel,
): NurturingPlanExecutionFilterModel | undefined {
  const statusItems = statusModel?.items ?? [];
  const dateItems = calendarDateModel?.items ?? [];

  if (!statusItems.length && !dateItems.length) {
    return undefined;
  }

  if (statusItems.length && !dateItems.length) {
    return statusModel;
  }

  if (!statusItems.length && dateItems.length) {
    return calendarDateModel;
  }

  const dateUsesOr =
    calendarDateModel?.linkOperator === 'or' && dateItems.length > 1;

  if (dateUsesOr) {
    return {
      items: statusItems.map((item, index) => ({ ...item, id: index + 1 })),
      linkOperator: 'and',
      orGroups: [{ items: dateItems }],
    };
  }

  return {
    items: [...statusItems, ...dateItems].map((item, index) => ({
      ...item,
      id: index + 1,
    })),
    linkOperator: 'and',
  };
}

export interface UseNurturingPlanExecutionFilterTrayArgs {
  dealerId: string;
  fetchEnabled?: boolean;
}

export interface UseNurturingPlanExecutionFilterTrayResult {
  categories: FilterTrayCategory[];
  optionsForActiveCategory: FilterTrayOption[];
  selectedByCategory: FilterTraySelection;
  onSelectionChange: (categoryId: string, selectedValues: string[]) => void;
  onClearAllFilters: () => void;
  onClearActiveCategory: () => void;
  onClearAllInPanel: () => void;
  executionFilterModel: NurturingPlanExecutionFilterModel | undefined;
  totalActiveCount: number;
  activeCategoryId: string | null;
  setActiveCategoryId: (id: string | null) => void;
  isLoading: boolean;
  error: Error | null;
}

export function useNurturingPlanExecutionFilterTray({
  dealerId,
  fetchEnabled = false,
}: UseNurturingPlanExecutionFilterTrayArgs): UseNurturingPlanExecutionFilterTrayResult {
  const [selectedByCategory, setSelectedByCategory] = useState<FilterTraySelection>(
    {},
  );
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);

  const selectedStatuses = selectedByCategory[EXECUTION_STATUS_FIELD];
  const selectedCalendarDates = selectedByCategory[CALENDAR_DATE_FIELD];

  const executionStatusFilterModel = useMemo(
    () => buildExecutionStatusFilterModel(selectedStatuses),
    [selectedStatuses],
  );

  const calendarDateFilterModel = useMemo(
    () => buildCalendarDateFilterModel(selectedCalendarDates),
    [selectedCalendarDates],
  );

  const executionFilterModel = useMemo(
    () =>
      buildCombinedExecutionFilterModel(
        executionStatusFilterModel,
        calendarDateFilterModel,
      ),
    [executionStatusFilterModel, calendarDateFilterModel],
  );

  const params = useMemo(
    () => ({
      dealer_id: dealerId,
      limit: FILTER_TRAY_PAGE_SIZE,
    }),
    [dealerId],
  );

  const filtersForFetch = useMemo(() => {
    const hasFilterItems = (executionFilterModel?.items?.length ?? 0) > 0;
    const hasOrGroups = (executionFilterModel?.orGroups?.length ?? 0) > 0;
    return hasFilterItems || hasOrGroups
      ? JSON.stringify(executionFilterModel)
      : '';
  }, [executionFilterModel]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [
      'nurturing-plan-execution-filter-tray',
      dealerId,
      params,
      filtersForFetch,
    ],
    queryFn: () =>
      fetchDataFromAPI(
        EXECUTIONS_ENDPOINT,
        params,
        1,
        FILTER_TRAY_PAGE_SIZE,
        filtersForFetch,
      ),
    enabled: Boolean(dealerId) && fetchEnabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const statusOptionsFromData = useMemo(() => {
    const rows = data?.body?.data ?? [];
    return deriveExecutionStatusValues(rows);
  }, [data]);

  const calendarDateOptionsFromData = useMemo(() => {
    const rows = data?.body?.data ?? [];
    return deriveCalendarDateKeys(rows);
  }, [data]);

  const categories: FilterTrayCategory[] = useMemo(() => {
    const next: FilterTrayCategory[] = [];

    if (
      statusOptionsFromData.length > 0 ||
      (selectedStatuses?.length ?? 0) > 0
    ) {
      next.push({
        id: EXECUTION_STATUS_FIELD,
        label: 'Execution Status',
        selectedCount: selectedStatuses?.length ?? 0,
      });
    }

    if (
      calendarDateOptionsFromData.length > 0 ||
      (selectedCalendarDates?.length ?? 0) > 0
    ) {
      next.push({
        id: CALENDAR_DATE_FIELD,
        label: 'Execution Date',
        selectedCount: selectedCalendarDates?.length ?? 0,
      });
    }

    return next;
  }, [
    selectedStatuses,
    selectedCalendarDates,
    statusOptionsFromData.length,
    calendarDateOptionsFromData.length,
  ]);

  const optionsForActiveCategory: FilterTrayOption[] = useMemo(() => {
    if (activeCategoryId === EXECUTION_STATUS_FIELD) {
      const applied = selectedStatuses ?? [];
      const combined = Array.from(
        new Set([...statusOptionsFromData, ...applied]),
      ).sort((a, b) => a.localeCompare(b));
      return combined.map(value => ({
        value,
        label: formatExecutionStatusLabel(value),
      }));
    }

    if (activeCategoryId === CALENDAR_DATE_FIELD) {
      const applied = selectedCalendarDates ?? [];
      const combined = Array.from(
        new Set([...calendarDateOptionsFromData, ...applied]),
      ).sort((a, b) => b.localeCompare(a));
      return combined.map(value => ({
        value,
        label: formatUtcDateKeyLabel(value),
      }));
    }

    return [];
  }, [
    activeCategoryId,
    statusOptionsFromData,
    selectedStatuses,
    calendarDateOptionsFromData,
    selectedCalendarDates,
  ]);

  const onSelectionChange = useCallback(
    (categoryId: string, selectedValues: string[]) => {
      setSelectedByCategory(previous => ({
        ...previous,
        [categoryId]: selectedValues,
      }));
    },
    [],
  );

  const onClearAllFilters = useCallback(() => {
    setSelectedByCategory({});
  }, []);

  const onClearActiveCategory = useCallback(() => {
    if (!activeCategoryId) return;
    setSelectedByCategory(previous => ({
      ...previous,
      [activeCategoryId]: [],
    }));
  }, [activeCategoryId]);

  const onClearAllInPanel = useCallback(() => {
    setSelectedByCategory({});
  }, []);

  const totalActiveCount = useMemo(() => {
    const statusCount = selectedStatuses?.length ?? 0;
    const calendarDateCount = selectedCalendarDates?.length ?? 0;
    return statusCount + calendarDateCount;
  }, [selectedStatuses, selectedCalendarDates]);

  return {
    categories,
    optionsForActiveCategory,
    selectedByCategory,
    onSelectionChange,
    onClearAllFilters,
    onClearActiveCategory,
    onClearAllInPanel,
    executionFilterModel,
    totalActiveCount,
    activeCategoryId,
    setActiveCategoryId,
    isLoading: !!isLoading || !!isFetching,
    error: error as Error | null,
  };
}
